import React from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import ColumnFilter from "../ReusableTable/FilterComponents/ColumnFilter";
import FilterManager from "../ReusableTable/FilterComponents/FilterManager";

/**
 * DialogTable — a compact table built for use inside MUI Dialogs.
 *
 * Unlike ReusableTable it does NOT enforce minWidth: 1024px, so it fits
 * naturally inside sm/md dialogs without horizontal scroll.
 *
 * Props mirror ReusableTable's subset relevant to dialogs:
 *   columns        – array of { label, field, width, align, filterConfig? }
 *   data           – array of row objects (must have an `id` field)
 *   renderCell     – (row, column) => ReactNode
 *   filters        – controlled filter state object
 *   onFilterChange – (filtersObj) => void
 *   getRowSx       – (row) => sx object
 *   getRowProps    – (row) => props spread onto TableRow
 *   loading        – show skeleton rows
 *   emptyMessage   – string shown when data is empty
 *   cellSx         – sx applied to every body TableCell
 *   headerCellSx   – sx applied to every header TableCell
 */
const DialogTable = ({
  columns = [],
  data = [],
  renderCell,
  filters = {},
  onFilterChange,
  getRowSx,
  getRowProps,
  loading = false,
  emptyMessage = "No data",
  cellSx = {},
  headerCellSx = {},
}) => {
  const filterManager = FilterManager({ onFilterChange, filters });

  return (
    <Table
      sx={{
        tableLayout: "fixed",
        width: "100%",
        borderCollapse: "collapse",
      }}
    >
      {/* colgroup for precise widths */}
      <colgroup>
        {columns.map((col, i) => (
          <col key={i} style={{ width: col.width || "auto" }} />
        ))}
      </colgroup>

      <TableHead>
        <TableRow sx={{ backgroundColor: "#F7F7FC" }}>
          {columns.map((col, i) => (
            <TableCell
              key={i}
              align={col.align || "left"}
              sx={{
                py: "6px",
                px: "8px",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
                whiteSpace: "nowrap",
                overflow: "hidden",
                ...headerCellSx,
              }}
            >
              {col.filterConfig ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {col.label}
                  </Box>
                  <ColumnFilter
                    column={col}
                    onFilterChange={filterManager.handleFilterChange}
                    filters={filterManager.getFilters(col.field)}
                  />
                </Box>
              ) : (
                col.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ py: 4, border: "none" }}>
              <CircularProgress size={24} />
            </TableCell>
          </TableRow>
        ) : data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: "text.secondary", border: "none" }}>
              <Typography variant="body2">{emptyMessage}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          data.map((row, rowIndex) => {
            const rowProps = getRowProps ? getRowProps(row, rowIndex) : {};
            const rowSx = getRowSx ? getRowSx(row, rowIndex) : {};
            return (
              <TableRow
                key={row.id ?? rowIndex}
                {...rowProps}
                sx={{
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                  ...rowSx,
                  ...(rowProps.sx || {}),
                }}
              >
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={col.align || "left"}
                    sx={{
                      py: "7px",
                      px: "8px",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      overflow: "hidden",
                      ...cellSx,
                    }}
                  >
                    {renderCell ? renderCell(row, col) : (row[col.field] ?? "—")}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
};

export default DialogTable;
